import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "@tanstack/react-router";

type Thread = {
  id: string;
  title: string;
  updatedAt: number;
  messages: UIMessage[];
};

const STORAGE_KEY = "kaf.guide.threads.v1";
const ACTIVE_KEY = "kaf.guide.active.v1";

function loadThreads(): Thread[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveThreads(threads: Thread[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(threads));
  } catch {
    /* ignore */
  }
}

function newThread(): Thread {
  return {
    id: `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    title: "New transmission",
    updatedAt: Date.now(),
    messages: [],
  };
}

function extractText(m: UIMessage): string {
  return m.parts
    .map((p) => (p.type === "text" ? p.text : ""))
    .join("")
    .trim();
}

type Source = {
  type: "music" | "activation" | "wisdom" | "book" | "nav" | "practice";
  slug?: string;
  title: string;
  route: string;
  note?: string;
};

// Parses a trailing "<!--SOURCES-->\n```json ... ```" provenance block emitted
// by the Guide. Returns cleaned body text and parsed sources.
function parseSources(raw: string): { body: string; sources: Source[] } {
  const marker = raw.lastIndexOf("<!--SOURCES-->");
  if (marker === -1) return { body: raw, sources: [] };
  const before = raw.slice(0, marker).trimEnd();
  const after = raw.slice(marker + "<!--SOURCES-->".length);
  const fence = after.match(/```(?:json)?\s*([\s\S]*?)```/);
  const jsonText = fence ? fence[1] : after;
  try {
    const parsed = JSON.parse(jsonText.trim());
    if (Array.isArray(parsed)) {
      const sources = parsed.filter(
        (s): s is Source =>
          s && typeof s.title === "string" && typeof s.route === "string"
      );
      return { body: before, sources };
    }
  } catch {
    /* keep body without sources */
  }
  return { body: before, sources: [] };
}

const TYPE_LABEL: Record<Source["type"], string> = {
  music: "Music",
  activation: "Activation",
  wisdom: "Teaching",
  book: "Book",
  nav: "Section",
  practice: "Practice",
};

const QUICK_PROMPTS = [
  "Recommend a song for grief",
  "A breathing exercise for anxiety",
  "Where do I begin?",
  "What should I read first?",
];

export function AIGuide() {
  const [open, setOpen] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [hydrated, setHydrated] = useState(false);
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // One-time hydration
  useEffect(() => {
    const existing = loadThreads();
    if (existing.length === 0) {
      const first = newThread();
      setThreads([first]);
      setActiveId(first.id);
      saveThreads([first]);
    } else {
      setThreads(existing);
      const savedActive = window.localStorage.getItem(ACTIVE_KEY);
      const found = existing.find((t) => t.id === savedActive);
      setActiveId(found ? found.id : existing[0].id);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (activeId) {
      try {
        window.localStorage.setItem(ACTIVE_KEY, activeId);
      } catch {
        /* ignore */
      }
    }
  }, [activeId]);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeId) ?? threads[0],
    [threads, activeId]
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/guide",
        fetch: async (input, init) => {
          const { supabase } = await import("@/integrations/supabase/client");
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          const headers = new Headers(init?.headers);
          if (token) headers.set("Authorization", `Bearer ${token}`);
          return fetch(input, { ...init, headers });
        },
      }),
    []
  );


  const { messages, sendMessage, status, setMessages } = useChat({
    id: activeThread?.id ?? "empty",
    messages: activeThread?.messages ?? [],
    transport,
    onError: (err: Error) => console.error("[guide]", err),
  });

  // When active thread changes, load its messages
  useEffect(() => {
    if (!activeThread) return;
    setMessages(activeThread.messages);
  }, [activeThread?.id, setMessages]);

  // Persist messages to active thread when they change
  useEffect(() => {
    if (!hydrated || !activeThread) return;
    if (messages === activeThread.messages) return;
    setThreads((prev) => {
      const next = prev.map((t) =>
        t.id === activeThread.id
          ? {
              ...t,
              messages,
              updatedAt: Date.now(),
              title:
                t.title === "New transmission" && messages[0]
                  ? extractText(messages[0]).slice(0, 42) || t.title
                  : t.title,
            }
          : t
      );
      saveThreads(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, hydrated, activeThread?.id]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, status]);

  // Focus input when opened / thread switched
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, activeId, status]);

  const isLoading = status === "submitted" || status === "streaming";

  const handleSend = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    void sendMessage({ text: trimmed });
    setInput("");
  };

  const handleNewThread = () => {
    const t = newThread();
    setThreads((prev) => {
      const next = [t, ...prev];
      saveThreads(next);
      return next;
    });
    setActiveId(t.id);
    setShowThreads(false);
  };

  const handleDeleteThread = (id: string) => {
    setThreads((prev) => {
      let next = prev.filter((t) => t.id !== id);
      if (next.length === 0) next = [newThread()];
      saveThreads(next);
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close guide" : "Open KAfrequency Guide"}
        className="fixed bottom-4 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gold-gradient text-obsidian shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)] transition hover:scale-105 active:scale-95 md:bottom-6 md:right-6"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M12 12v3M9 21c0-3 6-3 6 0M9 18h6" />
          </svg>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="KAfrequency Guide"
          className="fixed inset-x-2 bottom-20 z-40 flex h-[min(78vh,640px)] max-w-full flex-col overflow-hidden rounded-2xl border border-gold/25 bg-obsidian/95 shadow-2xl backdrop-blur-xl md:inset-x-auto md:right-6 md:bottom-24 md:w-[400px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gold/15 px-4 py-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">KAfrequency</p>
              <p className="truncate font-serif text-base text-gold-luminous">
                {activeThread?.title ?? "Guide"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowThreads((v) => !v)}
                aria-label="Conversations"
                className="rounded-full p-2 text-gold-luminous/70 hover:text-gold-luminous"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 6h16M4 12h16M4 18h10" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleNewThread}
                aria-label="New conversation"
                className="rounded-full p-2 text-gold-luminous/70 hover:text-gold-luminous"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </div>
          </div>

          {/* Threads drawer */}
          {showThreads && (
            <div className="max-h-48 overflow-y-auto border-b border-gold/15 bg-black/40">
              {threads
                .slice()
                .sort((a, b) => b.updatedAt - a.updatedAt)
                .map((t) => (
                  <div
                    key={t.id}
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${
                      t.id === activeId ? "bg-gold/5 text-gold-luminous" : "text-muted-foreground"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActiveId(t.id);
                        setShowThreads(false);
                      }}
                      className="flex-1 truncate text-left hover:text-gold-luminous"
                    >
                      {t.title}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteThread(t.id)}
                      aria-label="Delete conversation"
                      className="text-muted-foreground/60 hover:text-red-400"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
                      </svg>
                    </button>
                  </div>
                ))}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
            {messages.length === 0 ? (
              <div className="mx-auto max-w-sm text-center">
                <p className="font-serif text-xl text-gold-luminous">Ask the field.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Music, activations, breath, books, teachings — I'll guide you where to begin.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handleSend(p)}
                      className="rounded-full border border-gold/30 px-3 py-1.5 text-xs text-gold-luminous/90 hover:bg-gold/10"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((m: UIMessage) => {
                  const text = extractText(m);
                  const isUser = m.role === "user";
                  const { body, sources } = isUser
                    ? { body: text, sources: [] as Source[] }
                    : parseSources(text);
                  return (
                    <div key={m.id} className={isUser ? "flex justify-end" : ""}>
                      {isUser ? (
                        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-gold-gradient px-3.5 py-2 text-sm text-obsidian">
                          {text}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="prose prose-sm prose-invert max-w-none text-sm leading-relaxed text-foreground/90 prose-a:text-gold-luminous prose-a:no-underline hover:prose-a:underline prose-strong:text-gold-luminous">
                            <ReactMarkdown
                              components={{
                                a: ({ href, children }) => {
                                  const to = href ?? "";
                                  if (to.startsWith("/")) {
                                    return (
                                      <Link
                                        to={to}
                                        onClick={() => setOpen(false)}
                                        className="text-gold-luminous underline-offset-4 hover:underline"
                                      >
                                        {children}
                                      </Link>
                                    );
                                  }
                                  return (
                                    <a href={to} target="_blank" rel="noreferrer noopener">
                                      {children}
                                    </a>
                                  );
                                },
                              }}
                            >
                              {body || "…"}
                            </ReactMarkdown>
                          </div>
                          {sources.length > 0 && (
                            <div className="rounded-xl border border-gold/20 bg-black/30 p-3">
                              <div className="mb-2 flex items-center justify-between">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">
                                  Sources · KAfrequency library
                                </p>
                                <span
                                  className="text-[10px] text-muted-foreground/70"
                                  title="Every citation is drawn from the KAfrequency canonical content library — no external sources are used."
                                >
                                  verified
                                </span>
                              </div>
                              <ul className="space-y-1.5">
                                {sources.map((s, i) => (
                                  <li key={`${m.id}-src-${i}`} className="text-xs">
                                    <div className="flex items-baseline gap-2">
                                      <span className="inline-flex shrink-0 rounded-full border border-gold/30 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-gold-luminous/80">
                                        {TYPE_LABEL[s.type] ?? "Source"}
                                      </span>
                                      <Link
                                        to={s.route}
                                        onClick={() => setOpen(false)}
                                        className="truncate font-medium text-gold-luminous hover:underline"
                                      >
                                        {s.title}
                                      </Link>
                                    </div>
                                    {s.note && (
                                      <p className="ml-[3.25rem] mt-0.5 text-[11px] leading-snug text-muted-foreground">
                                        {s.note}
                                      </p>
                                    )}
                                  </li>
                                ))}
                              </ul>
                              <p className="mt-2 border-t border-gold/10 pt-2 text-[10px] leading-snug text-muted-foreground/70">
                                Citations are drawn from the KAfrequency canonical content library (music, activations, teachings, books). Follow each link to verify the teaching in its original context.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {status === "submitted" && (
                  <div className="text-sm italic text-gold-luminous/70">Tuning…</div>
                )}
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            className="border-t border-gold/15 bg-black/40 px-3 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend(input);
            }}
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(input);
                  }
                }}
                rows={1}
                placeholder="Ask the Guide…"
                className="max-h-32 min-h-[40px] flex-1 resize-none rounded-xl border border-gold/20 bg-obsidian/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-gold/60 focus:outline-none"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                aria-label="Send"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-gradient text-obsidian transition disabled:opacity-40"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12l16-8-6 16-2-7-8-1z" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
